/**
 * Die Momentanwerte der Ergebnisgrößen der Anlage — **vom Server gerechnet** (#43).
 *
 * Bis #43 rechnete diese Datei die Bilanz aus der Leistung der Zähler selbst nach: Vorzeichen
 * des Netzzählers, Richtung des Erzeugungszählers, netto mit Aufnahme. Mit mehreren
 * Erzeugungszählern und einer Aufnahme je Zähler wäre das die zweite Fassung des Graphen
 * gewesen, und die Querschnittsregel sagt: **Gerechnet und gedeutet wird in der API**
 * (HM-042, HM-110). Seither nimmt sie `conceptNow` aus `GET /sites/{id}/live` — den Graphen der
 * heute gültigen Fassung, mit den Momentanwerten aller seiner Zähler ausgewertet
 * (`evaluateInstant`, HM-148). Dieselbe Quelle wie die App und die Zusammensetzung.
 *
 * Gezeigt werden nur die Größen der Anlage selbst (ohne Subjekt) und nur Leistungen; was von
 * einem Zähler ohne frischen Wert abhängt, liefert der Server nicht — eine halbe Bilanz ist
 * keine.
 */
import type { ApiComponents } from "@hausmeter/sdk";

type ConceptNow = NonNullable<ApiComponents["schemas"]["LiveValuesResponse"]["conceptNow"]>;
type QuantityKey = ApiComponents["schemas"]["SiteEnergyTotal"]["quantityKey"];

/**
 * Ab wann ein Momentanwert keiner mehr ist: drei Sendetakte eines Lesekopfs
 * (60 s), mindestens fünf Minuten — dieselbe Frist wie bei der Erreichbarkeit
 * eines Geräts (HM-050). Ein älterer Wert stünde als „jetzt" da und wäre es nicht.
 */
export const LIVE_MAX_AGE_SECONDS = 5 * 60;

/** Die Momentanwerte je Ergebnisgröße, in Watt — nur, was der Server ableiten konnte. */
export type LiveNow = Partial<Record<QuantityKey, number>>;

export interface LiveNowResult {
  readonly values: LiveNow;
  /** Der älteste beteiligte Wert — `null`, wenn keiner frisch ist. */
  readonly measuredAt: string | null;
}

/** Die Größen, die eine Leistung haben und oben rechts an ihrer Kachel stehen. */
const POWER_KEYS: readonly QuantityKey[] = [
  "grid_import",
  "grid_export",
  "generation",
  "self_consumption",
  "consumption",
];

export function liveNow(conceptNow: ConceptNow | null | undefined): LiveNowResult {
  if (conceptNow == null || conceptNow.ageSeconds > LIVE_MAX_AGE_SECONDS) {
    return { values: {}, measuredAt: null };
  }
  const values: LiveNow = {};
  for (const output of conceptNow.outputs) {
    if (output.subjectKey !== null || !POWER_KEYS.includes(output.quantityKey)) continue;
    values[output.quantityKey] = output.watts;
  }
  return { values, measuredAt: conceptNow.measuredAt };
}
